/**
 * Sentry initialization — safe, opt-in, kill-switchable.
 *
 * Design rules:
 *  - Hardcoded DSN is a publishable identifier; safe to commit.
 *  - Init is wrapped in try/catch so a bad SDK call can never break boot.
 *  - Runs in BOTH dev preview and production so we actually see events.
 *    (Skipping dev was the reason no events were ever captured.)
 *  - No tracing / no Session Replay in v1.
 */
import * as Sentry from "@sentry/react";
import React from "react";
import { SUPABASE_URL } from "@/lib/supabaseConfig";

const SENTRY_DSN =
  "https://718e95042e4e7ad77e5bb7595afd5ae2@o4510143093604352.ingest.us.sentry.io/4511454883217408";

// Route envelopes through our same-origin edge function so ad-blockers /
// privacy extensions that filter *sentry.io cannot drop events.
const SENTRY_TUNNEL = `${SUPABASE_URL}/functions/v1/sentry-tunnel`;

let initialized = false;

export function isSentryInitialized() {
  return initialized;
}

export function initSentry() {
  try {
    if (!SENTRY_DSN) return;

    Sentry.init({
      dsn: SENTRY_DSN,
      tunnel: SENTRY_TUNNEL,
      environment: import.meta.env.MODE,
      release: (import.meta.env.VITE_SENTRY_RELEASE as string) || undefined,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
        "Non-Error promise rejection captured",
        "AbortError",
        "Load failed",
        "RESET_BLANK_CHECK",
      ],
      beforeSend(event) {
        const msg = event.exception?.values?.[0]?.value || "";
        if (msg.includes("AuthSessionMissingError")) return null;
        // Stale-chunk errors after a deploy are recovered by an automatic
        // reload; they are not real crashes, so keep them out of Sentry.
        const lower = msg.toLowerCase();
        if (
          lower.includes("dynamically imported module") ||
          lower.includes("importing a module script failed") ||
          lower.includes("failed to load module script") ||
          lower.includes("loading chunk")
        ) {
          return null;
        }

        // Sanitize Unipile branding leak in any error message
        if (event.exception?.values) {
          for (const ex of event.exception.values) {
            if (ex.value) ex.value = ex.value.replace(/unipile/gi, "linkedin-provider");
          }
        }
        return event;
      },
    });

    initialized = true;
    // Smoke breadcrumb so we can verify ingestion from the dashboard
    Sentry.addBreadcrumb({ category: "lifecycle", message: "sentry-init", level: "info" });
    // eslint-disable-next-line no-console
    console.info("[Sentry] initialized", { env: import.meta.env.MODE, tunnel: SENTRY_TUNNEL });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[Sentry] init failed (non-fatal)", e);
  }
}

export function setSentryUser(
  user: { id: string; email?: string | null } | null,
  workspaceId?: string | null,
) {
  if (!initialized) return;
  try {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email ?? undefined });
    } else {
      Sentry.setUser(null);
    }
    Sentry.setTag("workspace_id", workspaceId ?? "none");
  } catch {
    /* swallow */
  }
}

export function clearSentryUser() {
  if (!initialized) return;
  try {
    Sentry.setUser(null);
    Sentry.setTag("workspace_id", "none");
  } catch {
    /* swallow */
  }
}

export function reportError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    /* swallow */
  }
}

export function captureMessage(msg: string, extra?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    Sentry.captureMessage(msg, { level: "info", extra });
  } catch {
    /* swallow */
  }
}

/** React error boundary that reports to Sentry and renders a minimal fallback. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export function FallbackUI() {
  return React.createElement(
    "div",
    { style: { padding: 24, fontFamily: "system-ui", color: "#475569" } },
    React.createElement("h2", null, "Something went wrong"),
    React.createElement("p", null, "The error has been reported. Please refresh the page."),
  );
}
